import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article" | "product" | "service";
  name?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;
  keywords?: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
  breadcrumbs?: Array<{ name: string; item: string }>;
  noindex?: boolean;
  nofollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  alternateLocales?: string[];
  section?: string;
  tags?: string[];
  metaTitle?: string | null;
  metaDescription?: string | null;
}

export default function SEO({
  title,
  description,
  canonical,
  type = "website",
  name = "SoverX.ai",
  image = "/logo/og.jpg",
  imageAlt = "SoverX.ai - Plataforma de IA Corporativa Segura",
  imageWidth = "1200",
  imageHeight = "630",
  keywords,
  schema,
  breadcrumbs,
  noindex = false,
  nofollow = false,
  author = "SoverX.ai",
  publishedTime,
  modifiedTime,
  locale = "pt_BR",
  alternateLocales = ["en_US"],
  section,
  tags,
  metaTitle,
  metaDescription,
}: SEOProps) {
  const siteUrl = "https://soverx.ai";

  // Prioritize metaTitle and metaDescription over title and description
  const effectiveTitle = metaTitle || title;
  const effectiveDescription = metaDescription || description;

  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = image.startsWith("http") ? image : `${siteUrl}${image}`;

  // Robots directives
  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
    "max-image-preview:large",
    "max-snippet:-1",
    "max-video-preview:-1",
  ].join(", ");

  // Enhanced Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SoverX.ai",
    legalName: "SoverX.ai",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: `${siteUrl}/logo.png`,
      width: "512",
      height: "512",
    },
    description:
      "Plataforma de IA corporativa segura com governança, auditoria e compliance para ambientes regulados",
    foundingDate: "2024",
    foundingLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: "BR",
        addressLocality: "São Paulo",
      },
    },
    sameAs: [
      "https://www.linkedin.com/company/soverx/",
      "https://www.instagram.com/soverx.ai/",
      "https://www.facebook.com/soverXai",
      "https://www.youtube.com/@soverX-ai",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "comercial@soverx.ai",
      areaServed: "BR",
      availableLanguage: ["Portuguese", "English"],
    },
  };

  // WebSite Schema with Search Action
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "SoverX.ai",
    url: siteUrl,
    description:
      "IA corporativa 100% offline com governança e auditoria para ambientes regulados",
    inLanguage: locale,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteUrl}/blog?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: crumb.item.startsWith("http")
            ? crumb.item
            : `${siteUrl}${crumb.item}`,
        })),
      }
    : null;

  // Combine all schemas
  const allSchemas = [
    organizationSchema,
    websiteSchema,
    breadcrumbSchema,
    ...(Array.isArray(schema) ? schema : schema ? [schema] : []),
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={locale.split("_")[0]} />
      <title>{effectiveTitle}</title>
      <meta name="title" content={effectiveTitle} />
      <meta name="description" content={effectiveDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <link rel="canonical" href={fullUrl} />

      {/* Language and Geo Targeting */}
      <meta property="og:locale" content={locale} />
      {alternateLocales.map(altLocale => (
        <meta
          key={altLocale}
          property="og:locale:alternate"
          content={altLocale}
        />
      ))}
      <meta name="geo.region" content="BR-SP" />
      <meta name="geo.placename" content="São Paulo" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={name} />
      <meta property="og:title" content={effectiveTitle} />
      <meta property="og:description" content={effectiveDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:secure_url" content={fullImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:type" content="image/png" />

      {/* Article specific tags */}
      {type === "article" && (
        <>
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {author && <meta property="article:author" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags &&
            tags.map(tag => (
              <meta key={tag} property="article:tag" content={tag} />
            ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={effectiveTitle} />
      <meta name="twitter:description" content={effectiveDescription} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:creator" content="@soverx" />
      <meta name="twitter:site" content="@soverx" />

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#0A1F44" />
      <meta name="msapplication-TileColor" content="#0A1F44" />
      <meta name="application-name" content="SoverX.ai" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />
      <meta name="apple-mobile-web-app-title" content="SoverX" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": allSchemas,
        })}
      </script>
    </Helmet>
  );
}
